/* ==========================================================================
   ZAETH — KORU MATERIAL
   Lightweight reveal + image parallax. No dependencies.
   ========================================================================== */

(() => {
  'use strict';

  const initKoru = (root) => {
    if (!root || root.dataset.koruInitialized === 'true') return;

    root.dataset.koruInitialized = 'true';

    const hero = root.querySelector('.koru-material__hero');
    const image = root.querySelector('.koru-material__hero-image');

    // Reveal elements progressively.
    const revealItems = [
      root.querySelector('.koru-material__eyebrow'),
      root.querySelector('.koru-material__heading'),
      root.querySelector('.koru-material__subheading'),
      root.querySelector('.koru-material__divider'),
      root.querySelector('.koru-material__body'),
      root.querySelector('.koru-material__body--secondary'),
      root.querySelector('.koru-material__cta'),
      ...root.querySelectorAll('.koru-material__feature'),
      root.querySelector('.koru-material__footer-content')
    ].filter(Boolean);

    revealItems.forEach((item, index) => {
      item.setAttribute('data-koru-reveal', '');
      item.style.transitionDelay = `${Math.min(index * 55, 500)}ms`;
    });

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    // Intersection reveal
    if (!reducedMotion && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            root.classList.add('is-visible');
            obs.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -8% 0px'
        }
      );

      observer.observe(root);
    } else {
      root.classList.add('is-visible');
    }

    /*
      Desktop-only restrained parallax.

      Disabled on mobile/touch so the composition remains stable
      and premium on smaller screens.
    */
    if (
      hero &&
      image &&
      !reducedMotion &&
      window.matchMedia('(min-width: 990px)').matches
    ) {
      let raf = null;

      const updateParallax = () => {
        raf = null;

        const rect = hero.getBoundingClientRect();
        const viewport =
          window.innerHeight ||
          document.documentElement.clientHeight;

        // Don't calculate when section isn't visible.
        if (rect.bottom < 0 || rect.top > viewport) return;

        const progress =
          (viewport - rect.top) /
          (viewport + rect.height);

        const offset = (progress - 0.5) * 18;

        image.style.transform =
          `scale(1.02) translate3d(0, ${offset}px, 0)`;
      };

      const requestParallax = () => {
        if (raf) return;

        raf = window.requestAnimationFrame(updateParallax);
      };

      window.addEventListener(
        'scroll',
        requestParallax,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        requestParallax,
        { passive: true }
      );

      requestParallax();
    }
  };

  const boot = () => {
    document
      .querySelectorAll('[data-koru-material]')
      .forEach(initKoru);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  /*
    Shopify Theme Editor support.
    Re-initializes the section when Shopify dynamically
    loads it inside the customizer.
  */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const section =
        event.target?.querySelector?.(
          '[data-koru-material]'
        );

      if (section) {
        initKoru(section);
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        event.target?.querySelector?.(
          '[data-koru-material]'
        );

      if (section) {
        section.dataset.koruInitialized = 'false';
      }
    }
  );
})();